<template>
    <div id="app">

        <!-- Row Detail Popup -->
        <div v-if="selectedRecord" class="simple-popup" @click.self="selectedRecord = null">
            <div class="popup-content wide-popup">
                <div class="popup-header">
                    <h3>🔔 Notification Log Detail</h3>
                    <button @click="selectedRecord = null" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="detail-sections">
                        <div v-for="(chunk, ci) in detailChunks" :key="ci" class="detail-section">
                            <div class="detail-grid">
                                <div v-for="col in chunk" :key="col" class="detail-row">
                                    <strong>{{ formatHeader(col) }}:</strong>
                                    <span :class="getDetailCellClass(col, selectedRecord[col])">
                                        {{ formatCell(selectedRecord[col], col) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Header -->
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-icon"><span>🔔</span></div>
                    <h1 class="dashboard-title">IOT — Notification Report Log</h1>
                </div>
                <div class="header-controls">
                    <div class="live-indicator">
                        <div class="live-dot"></div>
                        <span style="color:white;font-size:0.875rem;">Live Log</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">

            <!-- Summary Stats -->
            <div class="stats-section">
                <div class="stats-header">
                    <h3 class="stats-title">Notification Overview</h3>
                    <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
                        <span>{{ statsVisible ? '📊' : '📈' }}</span>
                        {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
                    </button>
                </div>
                <transition name="slide-fade">
                    <div v-if="statsVisible" class="stats-wrapper">
                        <div class="stats-container">
                            <div class="stat-card total-stat">
                                <div class="stat-label">Total Records</div>
                                <div class="stat-value">{{ records.length }}</div>
                                <div class="stat-change positive"><span>📋</span> All log entries</div>
                            </div>
                            <div class="stat-card volume-stat">
                                <div class="stat-label">Filtered Records</div>
                                <div class="stat-value" style="color:#4CAF50;">{{ filteredRecords.length }}</div>
                                <div class="stat-change"><span>🔎</span> After filters</div>
                            </div>
                            <div class="stat-card product-stat" v-if="uniqueNotificationTypes.length > 0">
                                <div class="stat-label">Notification Types</div>
                                <div class="stat-value" style="color:#9C27B0;">{{ uniqueNotificationTypes.length }}
                                </div>
                                <div class="stat-change"><span>📡</span> Distinct types</div>
                            </div>
                            <div class="stat-card equipment-stat" v-if="uniqueReportTypes.length > 0">
                                <div class="stat-label">Report Types</div>
                                <div class="stat-value" style="color:#2196F3;">{{ uniqueReportTypes.length }}</div>
                                <div class="stat-change"><span>📊</span> Distinct report types</div>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Filters Section -->
            <div class="search-section" :class="{ collapsed: !filtersVisible }">
                <div class="filter-header">
                    <h3 class="filter-title">Data Filters</h3>
                    <div class="filter-header-buttons">
                        <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
                            <span>{{ filtersVisible ? '📁' : '📂' }}</span>
                            {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
                        </button>
                        <button class="reset-filter-header-btn" @click="resetFilters">
                            <span class="reset-icon">↻</span> Reset Filters
                        </button>
                    </div>
                </div>
                <transition name="slide-fade">
                    <div v-if="filtersVisible" class="search-controls-wrapper">
                        <div class="search-controls">

                            <div class="search-group">
                                <label class="search-label">Quick Search</label>
                                <input type="text" v-model="quickSearch" @input="applyFilters" class="search-input"
                                    placeholder="Search any field…" />
                            </div>

                            <!-- Notification Type Filter -->
                            <div class="search-group" v-if="uniqueNotificationTypes.length > 0">
                                <label class="search-label">
                                    Notification Type
                                    <span class="multi-badge orange-badge"
                                        v-if="selectedFilters.notificationType.length > 0">({{
                                        selectedFilters.notificationType.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('notificationType')">
                                        <span v-if="selectedFilters.notificationType.length === 0"
                                            class="selected-items">All types</span>
                                        <span
                                            v-else-if="selectedFilters.notificationType.length === uniqueNotificationTypes.length"
                                            class="selected-items">All types</span>
                                        <span v-else class="selected-items">{{ selectedFilters.notificationType.length
                                            }} selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.notificationType" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleFilterAll('notificationType')"
                                                    :checked="selectedFilters.notificationType.length === uniqueNotificationTypes.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="nt in uniqueNotificationTypes"
                                                :key="nt">
                                                <input type="checkbox" :value="nt"
                                                    v-model="selectedFilters.notificationType" @change="applyFilters" />
                                                <span>{{ nt || '(empty)' }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- IOT Report Type Filter -->
                            <div class="search-group" v-if="uniqueReportTypes.length > 0">
                                <label class="search-label">
                                    IOT Report Type
                                    <span class="multi-badge orange-badge"
                                        v-if="selectedFilters.iotReportType.length > 0">({{
                                        selectedFilters.iotReportType.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('iotReportType')">
                                        <span v-if="selectedFilters.iotReportType.length === 0"
                                            class="selected-items">All report types</span>
                                        <span
                                            v-else-if="selectedFilters.iotReportType.length === uniqueReportTypes.length"
                                            class="selected-items">All report types</span>
                                        <span v-else class="selected-items">{{ selectedFilters.iotReportType.length }}
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.iotReportType" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleFilterAll('iotReportType')"
                                                    :checked="selectedFilters.iotReportType.length === uniqueReportTypes.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="rt in uniqueReportTypes" :key="rt">
                                                <input type="checkbox" :value="rt"
                                                    v-model="selectedFilters.iotReportType" @change="applyFilters" />
                                                <span>{{ rt || '(empty)' }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Dynamic Column Filters -->
                            <div class="search-group" v-for="filterCol in dynamicFilterColumns" :key="filterCol">
                                <label class="search-label">
                                    {{ formatHeader(filterCol) }}
                                    <span class="multi-badge orange-badge"
                                        v-if="selectedFilters[filterCol] && selectedFilters[filterCol].length > 0">({{
                                        selectedFilters[filterCol].length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown(filterCol)">
                                        <span v-if="!selectedFilters[filterCol] || selectedFilters[filterCol].length === 0"
                                            class="selected-items">All values</span>
                                        <span
                                            v-else-if="selectedFilters[filterCol].length === getUniqueValues(filterCol).length"
                                            class="selected-items">All values</span>
                                        <span v-else class="selected-items">{{ selectedFilters[filterCol].length }}
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates[filterCol]" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleFilterAll(filterCol)"
                                                    :checked="selectedFilters[filterCol] && selectedFilters[filterCol].length === getUniqueValues(filterCol).length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="val in getUniqueValues(filterCol)" :key="val">
                                                <input type="checkbox" :value="val"
                                                    v-model="selectedFilters[filterCol]" @change="applyFilters" />
                                                <span>{{ val || '(empty)' }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Visible Columns -->
                            <div class="search-group" v-if="columns.length > 0">
                                <label class="search-label">
                                    Visible Columns
                                    <span class="multi-badge orange-badge">({{ visibleColumns.length }}/{{
                                        columns.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display columns-select" @click="toggleDropdown('columns')">
                                        <span v-if="visibleColumns.length === columns.length" class="selected-items">All
                                            columns selected</span>
                                        <span v-else class="selected-items">{{ visibleColumns.length }} columns
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.columns" class="multi-select-dropdown columns-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllColumns"
                                                    :checked="visibleColumns.length === columns.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="col in columns" :key="col">
                                                <input type="checkbox" :value="col" v-model="visibleColumns"
                                                    @change="onColumnToggle" />
                                                <span>{{ formatHeader(col) }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div class="filter-actions">
                            <button class="reset-filters-btn" @click="resetFilters">↺ Reset</button>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Data Grid -->
            <div class="grid-container">
                <div class="grid-header">
                    <div class="grid-title">
                        <span class="report-name">
                            🔔 Notification Log Entries
                            <span v-if="!loading && records.length > 0" class="record-count-badge">
                                {{ filteredRecords.length }} records
                            </span>
                        </span>
                    </div>
                    <div class="grid-controls">
                        <button v-if="hasActiveColumnSearches" class="clear-column-searches-btn"
                            @click="clearColumnSearches">
                            <span>✖</span><span>Clear Searches</span>
                        </button>
                        <div class="rows-selector">
                            <span class="rows-label">Show:</span>
                            <select v-model.number="itemsPerPage" @change="currentPage = 1" class="rows-select">
                                <option :value="10">10 rows</option>
                                <option :value="25">25 rows</option>
                                <option :value="50">50 rows</option>
                                <option :value="100">100 rows</option>
                                <option :value="200">200 rows</option>
                            </select>
                        </div>
                        <div class="grid-actions">
                            <button class="action-button popout-btn" @click="printReport">
                                <span>🖨️</span><span>Print</span>
                            </button>
                            <button class="action-button refresh-btn" @click="loadData">
                                <span>🔄</span><span>Refresh</span>
                            </button>
                            <button class="action-button export-btn" @click="exportCSV">
                                <span>📥</span><span>Export</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="table-container">
                    <div v-if="loading" class="loading-overlay">
                        <div class="loader"></div>
                    </div>

                    <div v-if="!loading && records.length === 0" class="empty-state">
                        <div class="empty-icon">📭</div>
                        <div class="empty-title">No Data Available</div>
                        <div class="empty-description">Notification logs will appear here as they are generated.
                        </div>
                    </div>

                    <table v-if="!loading && records.length > 0" class="data-table">
                        <thead>
                            <tr>
                                <th v-for="col in visibleColumns" :key="col" @click="sortBy(col)" class="sortable-th">
                                    {{ formatHeader(col) }}
                                    <span class="sort-indicator">
                                        {{ sort.col === col ? (sort.asc ? '▲' : '▼') : '⇅' }}
                                    </span>
                                </th>
                            </tr>
                            <tr class="search-row">
                                <th v-for="col in visibleColumns" :key="'s-' + col" class="search-cell">
                                    <input type="text" class="column-search-input" placeholder="Search..."
                                        v-model="colSearch[col]" @input="handleColumnSearch" />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="paginatedRecords.length === 0">
                                <td :colspan="visibleColumns.length" class="empty-state"
                                    style="padding:40px;text-align:center;">
                                    <div>No matching records — try clearing the filters.</div>
                                </td>
                            </tr>
                            <tr v-for="(rec, idx) in paginatedRecords" :key="idx" :class="getRowClass(rec)"
                                @click="selectedRecord = rec">
                                <td v-for="col in visibleColumns" :key="col">
                                    <!-- Notification type badge -->
                                    <span v-if="col === 'NotificationType' && rec[col]" class="badge-type">
                                        {{ rec[col] }}
                                    </span>
                                    <!-- Error field highlight -->
                                    <span v-else-if="col === 'Error' && rec[col]" class="error-cell" :title="rec[col]">
                                        {{ truncate(rec[col], 40) }}
                                    </span>
                                    <!-- Body/Message fields -->
                                    <span
                                        v-else-if="(col === 'Body' || col === 'NotificationMessage' || col === 'CustomMessage') && rec[col]"
                                        class="message-cell" :title="rec[col]">
                                        {{ truncate(rec[col], 50) }}
                                    </span>
                                    <!-- ID columns -->
                                    <code v-else-if="isIdCol(col)"
                                        class="id-code">{{ rec[col] != null ? rec[col] : '—' }}</code>
                                    <!-- Default -->
                                    <span v-else>{{ formatCell(rec[col], col) }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="totalPages > 1" class="pagination-container">
                    <div class="pagination-info">
                        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                        {{ Math.min(currentPage * itemsPerPage, filteredRecords.length) }}
                        of {{ filteredRecords.length }} entries
                    </div>
                    <div class="pagination-controls">
                        <button class="page-btn" @click="currentPage = 1" :disabled="currentPage === 1">First</button>
                        <button class="page-btn" @click="currentPage--" :disabled="currentPage === 1">Previous</button>
                        <button v-for="p in visiblePageNumbers" :key="p" class="page-btn page-number"
                            :class="{ active: p === currentPage }" @click="currentPage = p">{{ p }}</button>
                        <button class="page-btn" @click="currentPage++"
                            :disabled="currentPage === totalPages">Next</button>
                        <button class="page-btn" @click="currentPage = totalPages"
                            :disabled="currentPage === totalPages">Last</button>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    name: 'IOTNotificationReportLog',

    data: function () {
        return {
            loading: false,
            records: [],
            columns: [],
            visibleColumns: [],
            colSearch: {},
            quickSearch: '',
            filtersVisible: true,
            statsVisible: true,
            dropdownStates: {
                columns: false,
                notificationType: false,
                iotReportType: false
            },
            _handleClickOutsideBound: null,
            currentPage: 1,
            itemsPerPage: 25,
            sort: { col: null, asc: true },
            selectedRecord: null,
            selectedFilters: {
                notificationType: [],
                iotReportType: []
            },
            uniqueNotificationTypes: [],
            uniqueReportTypes: [],
            dynamicFilterColumns: []
        };
    },

    mounted: function () {
        var self = this;
        this._handleClickOutsideBound = function (e) { self.handleClickOutside(e); };
        document.addEventListener('click', this._handleClickOutsideBound);
        this.loadData();
    },

    beforeDestroy: function () {
        if (this._handleClickOutsideBound) document.removeEventListener('click', this._handleClickOutsideBound);
    },

    computed: {
        hasActiveColumnSearches: function () {
            var self = this;
            return this.columns.some(function (c) { return self.colSearch[c]; });
        },

        filteredRecords: function () {
            var self = this;
            var result = this.records.slice();

            if (this.quickSearch) {
                var qs = this.quickSearch.toLowerCase();
                result = result.filter(function (r) {
                    return self.columns.some(function (c) {
                        var v = r[c];
                        return v != null && String(v).toLowerCase().includes(qs);
                    });
                });
            }

            this.columns.forEach(function (col) {
                var term = (self.colSearch[col] || '').toLowerCase();
                if (!term) return;
                result = result.filter(function (r) {
                    var v = r[col];
                    return v != null && String(v).toLowerCase().includes(term);
                });
            });

            if (this.selectedFilters.notificationType.length > 0) {
                result = result.filter(function (r) {
                    return self.selectedFilters.notificationType.includes(r.NotificationType);
                });
            }

            if (this.selectedFilters.iotReportType.length > 0) {
                result = result.filter(function (r) {
                    return self.selectedFilters.iotReportType.includes(r.IOTReportTypeID);
                });
            }

            // Dynamic column filters
            self.dynamicFilterColumns.forEach(function (col) {
                if (self.selectedFilters[col] && self.selectedFilters[col].length > 0) {
                    result = result.filter(function (r) {
                        return self.selectedFilters[col].includes(r[col]);
                    });
                }
            });

            if (this.sort.col) {
                var sc = this.sort.col, asc = this.sort.asc;
                result = result.slice().sort(function (a, b) {
                    var av = a[sc] != null ? a[sc] : '', bv = b[sc] != null ? b[sc] : '';
                    var na = parseFloat(av), nb = parseFloat(bv);
                    if (!isNaN(na) && !isNaN(nb)) return asc ? na - nb : nb - na;
                    return asc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
                });
            }

            return result;
        },

        totalPages: function () { return Math.ceil(this.filteredRecords.length / this.itemsPerPage); },

        paginatedRecords: function () {
            var s = (this.currentPage - 1) * this.itemsPerPage;
            return this.filteredRecords.slice(s, s + this.itemsPerPage);
        },

        visiblePageNumbers: function () {
            var maxV = 5, pages = [], start = Math.max(1, this.currentPage - 2);
            var end = Math.min(this.totalPages, start + maxV - 1);
            if (end - start < maxV - 1) start = Math.max(1, end - maxV + 1);
            for (var i = start; i <= end; i++) pages.push(i);
            return pages;
        },

        detailChunks: function () {
            if (!this.selectedRecord) return [];
            var cols = this.columns, cs = Math.ceil(cols.length / 2), result = [];
            for (var i = 0; i < cols.length; i += cs) result.push(cols.slice(i, i + cs));
            return result;
        }
    },

    methods: {
        loadData: function () {
            var self = this;
            self.loading = true;
            self.records = [];
            self.columns = [];
            self.visibleColumns = [];
            self.colSearch = {};
            self.currentPage = 1;

            var url = 'IOTReporting/GetIotReportLog';

            var onSuccess = function (response) {
                debugger;
                var data = response.data && response.data.data ? response.data.data : response.data;
                if (!data || !data.length) {
                    self.loading = false;
                    return;
                }
                self.columns = Object.keys(data[0]);
                self.visibleColumns = self.columns.slice();
                self.columns.forEach(function (c) { self.$set(self.colSearch, c, ''); });
                self.records = data;
                self.extractUniqueFilterValues();
                self.loading = false;
            };

            var onError = function () { self.loading = false; };

            self.$ajaxGet(self, url, onSuccess, onError);
        },

        extractUniqueFilterValues: function () {
            var unique = [];
            this.records.forEach(function (r) {
                var v = r.NotificationType;
                if (v != null && v !== '' && !unique.includes(v)) unique.push(v);
            });
            this.uniqueNotificationTypes = unique.sort();

            var uniqueRt = [];
            this.records.forEach(function (r) {
                var v = r.IOTReportTypeID;
                if (v != null && !uniqueRt.includes(v)) uniqueRt.push(v);
            });
            this.uniqueReportTypes = uniqueRt.sort(function (a, b) { return a - b; });

            // Determine which columns to use as filters (exclude ID columns, message columns, timestamps, and specific fields)
            var self = this;
            var excludePatterns = ['id', 'message', 'body', 'error', 'timestamp', 'date', 'time', 'notificationtype', 'iotreporttypeid'];
            var filterColumns = this.columns.filter(function (col) {
                var lc = col.toLowerCase();
                var isExcluded = excludePatterns.some(function (pattern) {
                    return lc.includes(pattern);
                });
                return !isExcluded;
            });

            this.dynamicFilterColumns = filterColumns.slice(0, 3); // Limit to 3 dynamic filters to avoid clutter

            // Initialize dynamic filters
            this.dynamicFilterColumns.forEach(function (col) {
                self.selectedFilters[col] = [];
                self.$set(self.dropdownStates, col, false);
            });

            this.selectedFilters = Object.assign({}, this.selectedFilters, {
                notificationType: [],
                iotReportType: []
            });
        },

        getUniqueValues: function (col) {
            var unique = [];
            this.records.forEach(function (r) {
                var v = r[col];
                if (v != null && !unique.includes(v)) unique.push(v);
            });
            return unique.sort(function (a, b) {
                var aNum = parseFloat(a), bNum = parseFloat(b);
                if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                return String(a).localeCompare(String(b));
            });
        },

        applyFilters: function () { this.currentPage = 1; },
        handleColumnSearch: function () { this.currentPage = 1; },

        clearColumnSearches: function () {
            var self = this;
            this.columns.forEach(function (c) { self.$set(self.colSearch, c, ''); });
            this.currentPage = 1;
        },

        resetFilters: function () {
            var self = this;
            this.quickSearch = '';
            this.columns.forEach(function (c) { self.$set(self.colSearch, c, ''); });
            
            var newFilters = { notificationType: [], iotReportType: [] };
            this.dynamicFilterColumns.forEach(function (col) {
                newFilters[col] = [];
            });
            this.selectedFilters = newFilters;
            
            this.currentPage = 1;
            this.sort = { col: null, asc: true };
            this.visibleColumns = this.columns.slice();
        },

        toggleDropdown: function (key) {
            var self = this;
            Object.keys(this.dropdownStates).forEach(function (k) {
                self.dropdownStates[k] = k === key ? !self.dropdownStates[k] : false;
            });
        },

        toggleFilterAll: function (filterKey) {
            var filterArray = this.selectedFilters[filterKey];
            var uniqueArray = this.getUniqueValues(filterKey);
            
            if (filterArray && filterArray.length === uniqueArray.length) {
                this.selectedFilters[filterKey] = [];
            } else {
                this.selectedFilters[filterKey] = uniqueArray.slice();
            }
            this.applyFilters();
        },

        handleClickOutside: function (e) {
            var self = this;
            var hasClass = function (el, cls) { return el && el.classList && el.classList.contains(cls); };
            if (!hasClass(e.target, 'multi-select-display') && !e.target.closest('.multi-select-container')) {
                Object.keys(self.dropdownStates).forEach(function (k) { self.dropdownStates[k] = false; });
            }
        },

        toggleAllColumns: function () {
            if (this.visibleColumns.length === this.columns.length) {
                this.visibleColumns = [this.columns[0]];
            } else {
                this.visibleColumns = this.columns.slice();
            }
            this.currentPage = 1;
        },

        onColumnToggle: function () {
            if (!this.visibleColumns.length) this.visibleColumns = [this.columns[0]];
            this.currentPage = 1;
        },

        sortBy: function (col) {
            if (this.sort.col === col) this.sort.asc = !this.sort.asc;
            else { this.sort.col = col; this.sort.asc = true; }
            this.currentPage = 1;
        },

        formatHeader: function (col) {
            return col.replace(/_/g, ' ')
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        },

        formatCell: function (value, col) {
            if (value == null || value === '') return '—';
            return value;
        },

        truncate: function (value, len) {
            if (!value) return '—';
            var s = String(value);
            return s.length > len ? s.substring(0, len) + '…' : s;
        },

        isIdCol: function (col) {
            var lc = col.toLowerCase();
            return lc === 'id' || lc.endsWith('id') || lc.includes('accountid');
        },

        getRowClass: function (rec) {
            if (rec.Error && rec.Error.trim()) return 'row-failed';
            return '';
        },

        getDetailCellClass: function (col, value) {
            if (col === 'Error' && value) return 'error-text';
            return '';
        },

        exportCSV: function () {
            if (!this.filteredRecords.length) { alert('No data to export.'); return; }
            var cols = this.visibleColumns;
            var rows = [cols.map(function (c) { return '"' + c + '"'; }).join(',')];
            this.filteredRecords.forEach(function (r) {
                rows.push(cols.map(function (c) {
                    var v = r[c] != null ? r[c] : '';
                    return '"' + String(v).replace(/"/g, '""') + '"';
                }).join(','));
            });
            var a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' }));
            a.download = 'IOT_Notification_Log.csv';
            a.click();
        },

        printReport: function () { window.print(); }
    }
};
</script>

<style scoped>
* {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

#app {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #e2e8f0;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

/* Header */
.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
    flex-shrink: 0;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
}

.logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
}

.logo-icon {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
}

.dashboard-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 600;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.live-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, .2);
    padding: 8px 16px;
    border-radius: 20px;
}

.live-dot {
    width: 8px;
    height: 8px;
    background: #4CAF50;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, .7);
    }

    70% {
        box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
    }
}

/* Main */
.main-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #e2e8f0;
}

/* Stats */
.stats-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .06);
    border: 1px solid #e2e8f0;
}

.stats-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.stats-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1e293b;
}

.toggle-stats-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: .85rem;
    font-weight: 600;
    transition: opacity .2s;
}

.toggle-stats-btn:hover {
    opacity: .85;
}

.stats-wrapper {
    padding-top: 12px;
}

.stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.stat-card {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #e2e8f0;
    transition: transform .2s, box-shadow .2s, border-color .2s;
    cursor: default;
}

.stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, .12);
    border-color: #D2691E;
}

.stat-label {
    font-size: .8rem;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 8px;
}

.stat-value {
    font-size: 2.2rem;
    font-weight: 800;
    color: #1e293b;
    line-height: 1;
    margin-bottom: 10px;
}

.stat-change {
    font-size: .8rem;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
}

.stat-change.positive {
    color: #16a34a;
}

/* Filter Section */
.search-section {
    background: white;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .06);
    border: 1px solid #e2e8f0;
    transition: padding .3s;
}

.search-section.collapsed {
    padding-bottom: 16px;
}

.filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.filter-title {
    font-size: 1rem;
    font-weight: 600;
    color: #1e293b;
}

.filter-header-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
}

.toggle-filter-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: .8rem;
    font-weight: 500;
    transition: opacity .2s;
}

.toggle-filter-btn:hover {
    opacity: .85;
}

.reset-filter-header-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: white;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    cursor: pointer;
    font-size: .8rem;
    color: #475569;
    transition: all .2s;
    font-weight: 500;
}

.reset-filter-header-btn:hover {
    background: #fff8f3;
    border-color: #D2691E;
    color: #D2691E;
}

.reset-icon {
    font-weight: 700;
}

.search-controls-wrapper {
    padding-top: 4px;
}

.search-controls {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 14px;
    margin-bottom: 16px;
}

.search-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.search-label {
    font-size: .8rem;
    font-weight: 600;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 6px;
}

.search-input {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: .875rem;
    color: #374151;
    transition: border-color .2s, box-shadow .2s;
    background: white;
}

.search-input:focus {
    outline: none;
    border-color: #D2691E;
    box-shadow: 0 0 0 3px rgba(210, 105, 30, .1);
}

.multi-badge {
    font-size: .7rem;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 10px;
    background: #e2e8f0;
    color: #475569;
}

.orange-badge {
    background: #fff3e0;
    color: #D2691E;
}

.multi-select-container {
    position: relative;
}

.multi-select-display {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    transition: border-color .2s;
    font-size: .875rem;
}

.multi-select-display:hover {
    border-color: #D2691E;
}

.selected-items {
    color: #374151;
}

.dropdown-arrow {
    color: #9ca3af;
    font-size: .7rem;
}

.multi-select-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, .12);
    z-index: 1000;
    max-height: 220px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.columns-dropdown {
    min-width: 220px;
}

.select-all-option {
    padding: 8px 12px;
    border-bottom: 1px solid #f1f5f9;
    background: #f8fafc;
}

.dropdown-options {
    overflow-y: auto;
    flex: 1;
    padding: 6px 0;
}

.checkbox-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    cursor: pointer;
    font-size: .85rem;
    color: #374151;
    transition: background .15s;
}

.checkbox-option:hover {
    background: #f8fafc;
}

.checkbox-option input[type="checkbox"] {
    accent-color: #D2691E;
}

.filter-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    padding-top: 4px;
    border-top: 1px solid #f1f5f9;
}

.apply-filters-btn {
    padding: 9px 20px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: .875rem;
    font-weight: 600;
    transition: opacity .2s, transform .15s;
}

.apply-filters-btn:hover {
    opacity: .9;
    transform: translateY(-1px);
}

.reset-filters-btn {
    padding: 9px 20px;
    background: white;
    color: #475569;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    cursor: pointer;
    font-size: .875rem;
    font-weight: 500;
    transition: all .2s;
}

.reset-filters-btn:hover {
    background: #fff8f3;
    border-color: #D2691E;
    color: #D2691E;
}

/* Data Grid */
.grid-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .06);
    border: 1px solid #e2e8f0;
    overflow: hidden;
}

.grid-header {
    padding: 16px 20px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8fafc;
    flex-wrap: wrap;
    gap: 12px;
}

.grid-title {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.report-name {
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 8px;
}

.record-count-badge {
    font-size: .75rem;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    padding: 2px 10px;
    border-radius: 12px;
    font-weight: 600;
}

.grid-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.clear-column-searches-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: #fff3e0;
    border: 1px solid #ffcc80;
    border-radius: 6px;
    cursor: pointer;
    font-size: .8rem;
    color: #D2691E;
    font-weight: 500;
    transition: all .2s;
}

.clear-column-searches-btn:hover {
    background: #ffe0b2;
}

.rows-selector {
    display: flex;
    align-items: center;
    gap: 6px;
}

.rows-label {
    font-size: .8rem;
    color: #64748b;
    font-weight: 500;
}

.rows-select {
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: .85rem;
    color: #374151;
    background: white;
    cursor: pointer;
}

.grid-actions {
    display: flex;
    gap: 8px;
}

.action-button {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 7px 14px;
    border-radius: 6px;
    cursor: pointer;
    font-size: .8rem;
    font-weight: 500;
    border: 1px solid transparent;
    transition: all .2s;
}

.refresh-btn {
    background: white;
    border-color: #D2691E;
    color: #D2691E;
}

.refresh-btn:hover {
    background: #fff8f3;
}

.export-btn {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
}

.export-btn:hover {
    opacity: .9;
}

.popout-btn {
    background: white;
    border-color: #cbd5e1;
    color: #475569;
}

.popout-btn:hover {
    background: #f1f5f9;
}

.table-container {
    overflow-x: auto;
    max-height: 600px;
    overflow-y: auto;
    position: relative;
}

.loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, .8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.loader {
    width: 36px;
    height: 36px;
    border: 3px solid #e2e8f0;
    border-top-color: #D2691E;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    color: #94a3b8;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 12px;
}

.empty-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
}

.empty-description {
    font-size: .9rem;
}

/* Table */
.data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: .85rem;
}

.data-table thead {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    position: sticky;
    top: 0;
    z-index: 5;
}

.data-table th {
    padding: 12px 14px;
    text-align: left;
    font-weight: 600;
    color: white;
    white-space: nowrap;
    font-size: .8rem;
    letter-spacing: .02em;
}

.sortable-th {
    cursor: pointer;
    user-select: none;
    transition: background .2s;
}

.sortable-th:hover {
    background: rgba(255, 255, 255, .15);
}

.sort-indicator {
    margin-left: 4px;
    font-size: .7rem;
    opacity: .8;
}

.search-row {
    background: #fdf6f0;
}

.search-cell {
    padding: 6px 8px !important;
    background: transparent;
}

.column-search-input {
    width: 100%;
    padding: 5px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: .78rem;
    background: white;
    color: #374151;
    transition: border-color .2s;
}

.column-search-input:focus {
    outline: none;
    border-color: #D2691E;
    box-shadow: 0 0 0 2px rgba(210, 105, 30, .1);
}

.data-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
    transition: background .15s;
}

.data-table tbody tr:hover {
    background: #fff8f3;
}

.data-table td {
    padding: 10px 14px;
    color: #334155;
    white-space: nowrap;
}

/* Row states */
.row-failed {
    background: #fff5f5 !important;
}

.row-failed:hover {
    background: #fee2e2 !important;
}

/* Cell badges */
.badge-type {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: .75rem;
    font-weight: 600;
    background: #ede9fe;
    color: #6d28d9;
    border: 1px solid #ddd6fe;
}

.error-cell {
    color: #dc2626;
    font-size: .8rem;
    max-width: 200px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}

.error-text {
    color: #dc2626;
}

.message-cell {
    color: #475569;
    font-size: .8rem;
    max-width: 250px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}

.id-code {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: .78rem;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    color: #475569;
    border: 1px solid #e2e8f0;
}

/* Pagination */
.pagination-container {
    padding: 14px 20px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8fafc;
    flex-wrap: wrap;
    gap: 10px;
}

.pagination-info {
    font-size: .8rem;
    color: #64748b;
    font-weight: 500;
}

.pagination-controls {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
}

.page-btn {
    padding: 6px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    font-size: .8rem;
    color: #475569;
    transition: all .2s;
    font-weight: 500;
}

.page-btn:hover:not(:disabled) {
    background: #fff8f3;
    border-color: #D2691E;
    color: #D2691E;
    transform: translateY(-1px);
}

.page-btn:disabled {
    opacity: .4;
    cursor: not-allowed;
}

.page-btn.active {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-color: #D2691E;
    font-weight: 700;
}

/* Popup */
.simple-popup {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .5);
    z-index: 9000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.popup-content {
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, .25);
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    overflow: hidden;
    border: 1px solid #cbd5e1;
}

.wide-popup {
    width: 900px;
    max-width: 95vw;
}

.popup-header {
    padding: 16px 20px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.popup-header h3 {
    color: white;
    font-size: 1rem;
    font-weight: 600;
}

.close-btn {
    background: rgba(255, 255, 255, .2);
    border: none;
    color: white;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .2s;
}

.close-btn:hover {
    background: rgba(255, 255, 255, .4);
}

.popup-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
}

.detail-sections {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.detail-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 14px;
    border: 1px solid #e2e8f0;
}

.detail-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detail-row {
    font-size: .875rem;
    color: #334155;
    padding: 4px 0;
    border-bottom: 1px solid #f1f5f9;
}

.detail-row strong {
    color: #1e293b;
    margin-right: 4px;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all .3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

@media (max-width: 768px) {
    .main-content {
        padding: 12px;
    }

    .stats-container {
        grid-template-columns: 1fr 1fr;
    }

    .search-controls {
        grid-template-columns: 1fr;
    }

    .detail-sections {
        grid-template-columns: 1fr;
    }

    .data-table {
        font-size: .75rem;
    }

    .data-table th,
    .data-table td {
        padding: 8px;
    }
}

@media print {

    .header-toolbar,
    .search-section,
    .stats-section,
    .pagination-container,
    .simple-popup {
        display: none !important;
    }

    .grid-container {
        box-shadow: none;
        border: none;
    }

    .table-container {
        max-height: none !important;
        overflow: visible !important;
    }

    .data-table thead {
        background: #ccc !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
}
</style>